import React, { useState, useEffect } from 'react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiPackage,
  FiDollarSign,
  FiTag,
  FiEye,
  FiX,
  FiSave,
  FiHash,
  FiFileText,
  FiActivity,
  FiZap,
  FiBox,
  FiGrid,
  FiShield,
  FiBarChart,
  FiEdit,
  FiTarget
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from '../utils/axiosConfig';
import LoadingSpinner from '../components/LoadingSpinner';
import styles from './PacientesNew.module.css';

const Medicamentos = () => {
  const [medicamentos, setMedicamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingMedicamento, setEditingMedicamento] = useState(null);
  const [formData, setFormData] = useState({
    codigo_barras: '',
    nome_comercial: '',
    nome_generico: '',
    principio_ativo: '',
    laboratorio: '',
    forma_farmaceutica: 'comprimido',
    concentracao: '',
    apresentacao: '',
    unidade_inventario: 'unidade',
    preco_por_ui: '',
    medicamento_controlado: false,
    registro_ms: '',
    estoque_minimo: '0',
    observacoes: ''
  });

  useEffect(() => {
    fetchMedicamentos();
  }, []);

  const fetchMedicamentos = async () => {
    try {
      const response = await axios.get('/farmacia/medicamentos');
      if (response.data.success) {
        setMedicamentos(response.data.data.medicamentos || []);
      }
    } catch (error) {
      console.error('Erro ao carregar medicamentos:', error);
      toast.error('Erro ao carregar medicamentos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      codigo_barras: '',
      nome_comercial: '',
      nome_generico: '',
      principio_ativo: '',
      laboratorio: '',
      forma_farmaceutica: 'comprimido',
      concentracao: '',
      apresentacao: '',
      unidade_inventario: 'unidade',
      preco_por_ui: '',
      medicamento_controlado: false,
      registro_ms: '',
      estoque_minimo: '0',
      observacoes: ''
    });
    setEditingMedicamento(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingMedicamento) {
        const response = await axios.put(`/farmacia/medicamentos/${editingMedicamento.id}`, formData);
        if (response.data.success) {
          toast.success('Medicamento atualizado com sucesso!');
        }
      } else {
        const response = await axios.post('/farmacia/medicamentos', formData);
        if (response.data.success) {
          toast.success('Medicamento cadastrado com sucesso!');
        }
      }
      
      setShowModal(false);
      resetForm();
      fetchMedicamentos();
    } catch (error) {
      console.error('Erro ao salvar medicamento:', error);
      toast.error(editingMedicamento ? 'Erro ao atualizar medicamento' : 'Erro ao cadastrar medicamento');
    }
  };

  const handleEdit = (medicamento) => {
    setEditingMedicamento(medicamento);
    setFormData({
      codigo_barras: medicamento.codigo_barras || '',
      nome_comercial: medicamento.nome_comercial || '',
      nome_generico: medicamento.nome_generico || '',
      principio_ativo: medicamento.principio_ativo || '',
      laboratorio: medicamento.laboratorio || '',
      forma_farmaceutica: medicamento.forma_farmaceutica || 'comprimido',
      concentracao: medicamento.concentracao || '',
      apresentacao: medicamento.apresentacao || '',
      unidade_inventario: medicamento.unidade_inventario || 'unidade',
      preco_por_ui: medicamento.preco_por_ui || '',
      medicamento_controlado: medicamento.medicamento_controlado || false,
      registro_ms: medicamento.registro_ms || '',
      estoque_minimo: medicamento.estoque_minimo || '0',
      observacoes: medicamento.observacoes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja desativar este medicamento?')) {
      try {
        const response = await axios.delete(`/farmacia/medicamentos/${id}`);
        if (response.data.success) {
          toast.success('Medicamento desativado com sucesso!');
          fetchMedicamentos();
        }
      } catch (error) {
        console.error('Erro ao desativar medicamento:', error);
        toast.error('Erro ao desativar medicamento');
      }
    }
  };

  const filteredMedicamentos = medicamentos.filter(medicamento =>
    medicamento.nome_comercial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medicamento.nome_generico?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medicamento.principio_ativo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medicamento.laboratorio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medicamento.forma_farmaceutica?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medicamento.codigo_barras?.includes(searchTerm)
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            <FiPackage />
            Medicamentos
          </h1>
          <p className={styles.subtitle}>
            Gerencie o cadastro de medicamentos da farmácia
          </p>
        </div>
        <button 
          className="formButton formButtonPrimary"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <FiPlus />
          Novo Medicamento
        </button>
      </div>

      <div className={styles.controls}>
        <div className={styles.searchSection}>
          <div className={styles.searchContainer}>
            <div className={styles.searchBox}>
              <FiSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Pesquisar por nome, princípio ativo, laboratório..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  type="button"
                  className={styles.clearSearch}
                  onClick={() => setSearchTerm('')}
                >
                  <FiX />
                </button>
              )}
            </div>
            <div className={styles.searchInfo}>
              <span className={styles.searchResults}>
                {filteredMedicamentos.length} de {medicamentos.length} medicamentos
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <FiPackage />
            </div>
            <div className={styles.statInfo}>
              <h3>{medicamentos.length}</h3>
              <p>Total de Medicamentos</p>
              <small className={styles.statDetail}>
                {filteredMedicamentos.length !== medicamentos.length ? 
                  `${filteredMedicamentos.length} na busca atual` : 
                  'Todos os medicamentos cadastrados'
                }
              </small>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <FiDollarSign />
            </div>
            <div className={styles.statInfo}>
              <h3>
                R$ {medicamentos
                  .reduce((total, med) => total + (parseFloat(med.preco_por_ui) || 0), 0)
                  .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }
              </h3>
              <p>Valor Total Cadastrado</p>
              <small className={styles.statDetail}>
                Soma de todos os preços por unidade
              </small>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <FiShield />
            </div>
            <div className={styles.statInfo}>
              <h3>{medicamentos.filter(med => med.medicamento_controlado).length}</h3>
              <p>Medicamentos Controlados</p>
              <small className={styles.statDetail}>
                {((medicamentos.filter(med => med.medicamento_controlado).length / medicamentos.length) * 100).toFixed(1)}% do total
              </small>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <FiActivity />
            </div>
            <div className={styles.statInfo}>
              <h3>{medicamentos.filter(med => !med.medicamento_controlado).length}</h3>
              <p>Medicamentos Regulares</p>
              <small className={styles.statDetail}>
                Sem necessidade de controle especial
              </small>
            </div>
          </div>
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nome Comercial</th>
                <th>Nome Genérico</th>
                <th>Forma</th>
                <th>Concentração</th>
                <th>Preço/UI</th>
                <th>Estoque</th>
                <th>Controlado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredMedicamentos.map((medicamento) => (
                <tr key={medicamento.id}>
                  <td className={styles.nameCell}>
                    <strong>{medicamento.nome_comercial}</strong>
                  </td>
                  <td>{medicamento.nome_generico}</td>
                  <td>
                    <span className={styles.badge}>
                      {medicamento.forma_farmaceutica}
                    </span>
                  </td>
                  <td>{medicamento.concentracao}</td>
                  <td className={styles.priceCell}>
                    R$ {parseFloat(medicamento.preco_por_ui || 0).toFixed(2)}
                    <small>/{medicamento.unidade_inventario}</small>
                  </td>
                  <td>
                    <span className={styles.stockBadge}>
                      {medicamento.estoque_total || 0} {medicamento.unidade_inventario}
                    </span>
                  </td>
                  <td>
                    {medicamento.medicamento_controlado ? (
                      <span className={styles.controlledBadge}>Controlado</span>
                    ) : (
                      <span className={styles.regularBadge}>Regular</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.actionButtons} style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className={styles.editButton}
                        onClick={() => handleEdit(medicamento)}
                        title="Editar"
                        style={{ 
                          padding: '8px', 
                          background: '#007bff', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(medicamento.id)}
                        title="Desativar"
                        style={{ 
                          padding: '8px', 
                          background: '#dc3545', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FiTrash2 />
                      </button>
                      <button
                        onClick={() => alert(`Detalhes do medicamento:\n\nNome: ${medicamento.nome_comercial}\nGenérico: ${medicamento.nome_generico}\nPreço: R$ ${parseFloat(medicamento.preco_por_ui || 0).toFixed(2)}\nEstoque: ${medicamento.estoque_total || 0}`)}
                        title="Ver Detalhes"
                        style={{ 
                          padding: '8px', 
                          background: '#28a745', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FiEye />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredMedicamentos.length === 0 && (
            <div className={styles.emptyState}>
              <FiPackage size={48} />
              <h3>Nenhum medicamento encontrado</h3>
              <p>
                {searchTerm ? 
                  'Tente pesquisar com outros termos.' : 
                  'Comece cadastrando seu primeiro medicamento.'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>
                {editingMedicamento ? 'Editar Medicamento' : 'Novo Medicamento'}
              </h2>
              <button 
                className={styles.closeButton}
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <form onSubmit={handleSubmit} className="form">
              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Código de Barras</label>
                    <div className="formInputWrapper">
                      <FiHash className="formInputIcon" />
                      <input
                        type="text"
                        name="codigo_barras"
                        value={formData.codigo_barras}
                        onChange={handleInputChange}
                        placeholder="7891234567890"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">Nome Comercial</label>
                    <div className="formInputWrapper">
                      <FiTag className="formInputIcon" />
                      <input
                        type="text"
                        name="nome_comercial"
                        value={formData.nome_comercial}
                        onChange={handleInputChange}
                        required
                        placeholder="Ex: Dipirona 500mg"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Nome Genérico</label>
                    <div className="formInputWrapper">
                      <FiFileText className="formInputIcon" />
                      <input
                        type="text"
                        name="nome_generico"
                        value={formData.nome_generico}
                        onChange={handleInputChange}
                        placeholder="Ex: Dipirona Sódica"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Princípio Ativo</label>
                    <div className="formInputWrapper">
                      <FiTarget className="formInputIcon" />
                      <input
                        type="text"
                        name="principio_ativo"
                        value={formData.principio_ativo}
                        onChange={handleInputChange}
                        placeholder="Ex: Dipirona Sódica"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Laboratório</label>
                    <div className="formInputWrapper">
                      <FiActivity className="formInputIcon" />
                      <input
                        type="text"
                        name="laboratorio"
                        value={formData.laboratorio}
                        onChange={handleInputChange}
                        placeholder="Ex: Genérico Lab"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Forma Farmacêutica</label>
                    <div className="formInputWrapper">
                      <FiPackage className="formInputIcon" />
                      <select
                        value={formData.forma_farmaceutica}
                        onChange={handleInputChange}
                        className="formSelect"
                      >
                        <option value="comprimido">Comprimido</option>
                        <option value="capsula">Cápsula</option>
                        <option value="ampola">Ampola</option>
                        <option value="frasco">Frasco</option>
                        <option value="pomada">Pomada</option>
                        <option value="creme">Creme</option>
                        <option value="spray">Spray</option>
                        <option value="gotas">Gotas</option>
                        <option value="xarope">Xarope</option>
                        <option value="supositorio">Supositório</option>
                        <option value="outros">Outros</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Concentração</label>
                    <div className="formInputWrapper">
                      <FiZap className="formInputIcon" />
                      <input
                        type="text"
                        name="concentracao"
                        value={formData.concentracao}
                        onChange={handleInputChange}
                        placeholder="Ex: 500mg"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Apresentação</label>
                    <div className="formInputWrapper">
                      <FiBox className="formInputIcon" />
                      <input
                        type="text"
                        name="apresentacao"
                        value={formData.apresentacao}
                        onChange={handleInputChange}
                        placeholder="Ex: Caixa com 10 comprimidos"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Unidade de Inventário</label>
                    <div className="formInputWrapper">
                      <FiGrid className="formInputIcon" />
                      <input
                        type="text"
                        name="unidade_inventario"
                        value={formData.unidade_inventario}
                        onChange={handleInputChange}
                        placeholder="Ex: comprimido, ampola, frasco"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel">Preço por UI</label>
                    <div className="formInputWrapper">
                      <FiDollarSign className="formInputIcon" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        name="preco_por_ui"
                        value={formData.preco_por_ui}
                        onChange={handleInputChange}
                        required
                        placeholder="0.00"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Registro MS</label>
                    <div className="formInputWrapper">
                      <FiShield className="formInputIcon" />
                      <input
                        type="text"
                        name="registro_ms"
                        value={formData.registro_ms}
                        onChange={handleInputChange}
                        placeholder="Ex: 1.0000.0000"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel">Estoque Mínimo</label>
                    <div className="formInputWrapper">
                      <FiBarChart className="formInputIcon" />
                      <input
                        type="number"
                        min="0"
                        name="estoque_minimo"
                        value={formData.estoque_minimo}
                        onChange={handleInputChange}
                        placeholder="0"
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="formGroup">
                <div className="formCheckbox">
                  <input
                    type="checkbox"
                    id="medicamento_controlado"
                    name="medicamento_controlado"
                    checked={formData.medicamento_controlado}
                    onChange={handleInputChange}
                    className="formCheckboxInput"
                  />
                  <label htmlFor="medicamento_controlado" className="formCheckboxLabel">
                    <FiShield className="formCheckboxIcon" />
                    Medicamento Controlado
                  </label>
                </div>
              </div>

              <div className="formGroup">
                <label className="formLabel">Observações</label>
                <div className="formInputWrapper">
                  <FiEdit className="formInputIcon" />
                  <textarea
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Observações adicionais sobre o medicamento..."
                    className="formTextarea"
                  />
                </div>
              </div>

              <div className="formActions">
                <button
                  type="button"
                  className="formButton formButtonSecondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </button>
                <button type="submit" className="formButton formButtonPrimary">
                  <FiSave />
                  {editingMedicamento ? 'Atualizar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default Medicamentos;